import React from 'react';
import {
  Avatar,
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Business as BusinessIcon, Visibility } from '@mui/icons-material';
import { Business } from '../../../validation/admin.validation';
import { maskEINLast4 } from '../../../helper/einMask.helper';

type Props = {
  businesses: Business[];
  onViewDetail: (id: string) => void;
};

const getOwnerName = (business: Business) =>
  [business.ownerFirstName, business.ownerMiddleName, business.ownerLastName]
    .filter(Boolean)
    .join(' ') || '-';

const getWebsiteHref = (website: string) =>
  website?.startsWith('http') ? website : `https://${website}`;

const BusinessTableView: React.FC<Props> = ({ businesses, onViewDetail }) => {
  return (
    <div className="overflow-x-auto mb-6 lg:mb-8">
      <TableContainer component={Paper} sx={{ borderRadius: 3, minWidth: 900 }}>
        <Table sx={{ borderCollapse: 'collapse' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f9fafb' }}>
              {['Business', 'Owner', 'EIN', 'Email', 'Phone', 'Website', 'Actions'].map((header) => (
                <TableCell
                  key={header}
                  sx={{
                    fontWeight: 'bold',
                    border: '1px solid #e5e7eb',
                    textAlign: header === 'Actions' ? 'center' : 'left',
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {businesses.map((business) => (
              <TableRow key={business.id} hover>
                <TableCell sx={{ border: '1px solid #e5e7eb' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ bgcolor: '#eff6ff', color: '#2563eb', width: 32, height: 32 }}>
                      <BusinessIcon fontSize="small" />
                    </Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {business.name || '-'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={{ border: '1px solid #e5e7eb' }}>{getOwnerName(business)}</TableCell>
                <TableCell sx={{ border: '1px solid #e5e7eb' }}>{maskEINLast4(business.ein)}</TableCell>
                <TableCell sx={{ border: '1px solid #e5e7eb' }}>{business.email || '-'}</TableCell>
                <TableCell sx={{ border: '1px solid #e5e7eb' }}>{business.phone || '-'}</TableCell>
                <TableCell sx={{ border: '1px solid #e5e7eb', maxWidth: 220 }}>
                  {business.website ? (
                    <a
                      href={getWebsiteHref(business.website)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-blue-600 hover:text-blue-800 underline block truncate"
                    >
                      {business.website}
                    </a>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    border: '1px solid #e5e7eb',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <Tooltip title="View Details">
                    <IconButton color="info" size="small" onClick={() => onViewDetail(business.id)}>
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default BusinessTableView;
